/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import axios from 'axios';
import moment from 'moment';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import * as securityHelper from '../../helpers/securityHelper';
import Heading from './Heading';
import Spinner from './Spinner';


export default class CSRFacilityInformation extends React.Component {
    static propTypes = {
        /** The inventory type */
        inventory: PropTypes.oneOf(['Campground', 'Permit', 'Vehicle Permit', 'Ticket Facility']),
        /** The facility ID */
        facility_id: PropTypes.string
    };

    constructor(props) {
        super(props);

        this.VEHICLE_PERMIT = 'Vehicle Permit';
        this.PERMIT = 'Permit';
        this.TICKET_FACILITY = 'Ticket Facility';
        this.CAMPGROUND = 'Campground';

        this.linkMap = {
          [this.CAMPGROUND]: {
            facility: `${process.env.SITE_ROOT}/camping/campgrounds/:location_id`,
            factType: 'camping'
          },
          [this.PERMIT]: {
            facility: `${process.env.SITE_ROOT}/permits/:location_id`,
            factType: 'permits'
          },
          [this.TICKET_FACILITY]: {
            facility: `${process.env.SITE_ROOT}/ticket/facility/:location_id`,
            factType: 'ticket'
          },
          [this.VEHICLE_PERMIT]: {
            facility: `${process.env.SITE_ROOT}/vehiclepermits/:location_id`,
            factType: 'vehiclepermits'
          }
        };

        this.typedEnvironmentMap = {
            [this.CAMPGROUND]: {
              path: 'camps/internal/campgrounds',
              data: {
                additional: 'campground.notices',
                seasons: 'seasons'
              }
            },
            [this.PERMIT]: {
              path: 'permits',
              data: {
                additional: 'payload.description.need_to_know_permit'
              }
            },
            [this.TICKET_FACILITY]: {
              path: 'ticket/facility',
              data: {
                additional: 'notes'
              }
            },
            [this.VEHICLE_PERMIT]: {
              path: 'vehiclepermits',
              data: {
                additional: 'payload.description.need_to_know_permit'
              }
            }
        };

        this.state = {
            isExternalCSRLoggedIn: false,

            locationData: null,
            locationError: null,
            locationLoading: false,

            additionalData: null,
            additionalError: null,
            additionalLoading: false,

            seasonsData: null,
            seasonsError: null,
            seasonsLoading: false,

            bannerData: null,
            bannerError: null,
            bannerLoading: false
        }

        this.fetchData = this.fetchData.bind(this);
        this.fetchAllData = this.fetchAllData.bind(this);
        this.renderBanners = this.renderBanners.bind(this);
        this.renderList = this.renderList.bind(this);
        this.renderSeasonTable = this.renderSeasonTable.bind(this);
        this.isValidLocation = this.isValidLocation.bind(this);
    }

    componentDidMount() {
        this.setState({
            isExternalCSRLoggedIn: securityHelper.isExternalCSRLoggedIn()
        })

        this.fetchAllData();
    }

    componentDidUpdate(prevProps, prevState) {
        if(prevProps.inventory !== this.props.inventory || prevProps.facility_id !== this.props.facility_id || (!prevState.isExternalCSRLoggedIn && this.state.isExternalCSRLoggedIn)) {
            this.fetchAllData();
        }
    }

    fetchAllData() {
        if (!this.state.isExternalCSRLoggedIn) {
            return;
        }

        if(!this.isValidLocation()) {
            return;
        }

        // Fire off our API calls to get our information
        this.fetchData(
            `${process.env.API}/internal/account/location/${this.props.inventory}/${this.props.facility_id}/info`,
            "locationData",
            "locationError",
            "locationLoading",
            "location_info"
        );

        const additional = get(this.typedEnvironmentMap, this.props.inventory, false);
        if (additional) {
            this.fetchData(
                `${process.env.API}/${additional.path}/${this.props.facility_id}`,
                "additionalData",
                "additionalError",
                "additionalLoading",
                additional.data.additional
            )

            const seasons = get(additional, "data.seasons", false);
            if (seasons) {
                this.fetchData(
                    `${process.env.API}/${additional.path}/${this.props.facility_id}/${seasons}`,
                    "seasonsData",
                    "seasonsError",
                    "seasonsLoading",
                    seasons
                )
            }
        }

        const bannerUrl = `${process.env.API}/communication/external/alert`;
        const bannerParams = `location_id=${encodeURIComponent(this.props.facility_id)}&location_type=${encodeURIComponent(this.props.inventory)}`;
        this.fetchData(
            `${bannerUrl}?${bannerParams}`,
            "bannerData",
            "bannerError",
            "bannerLoading",
            "alerts"
        )
    }

    fetchData(url, dataStateVariableName, errorStateVariableName, loadingStateVariableName, responseKey) {
        const internalAuthHeader = {
            Authorization: securityHelper.getExternalCSRAuthHeader()
        };

        this.setState({
            [loadingStateVariableName]: true
        });

        axios.get(url,
            {
                headers: internalAuthHeader
            })
            .then((result) => {
                this.setState({
                    [loadingStateVariableName]: false,
                    [errorStateVariableName]: null,
                    [dataStateVariableName]: get(result.data, responseKey, {})
                })
            })
            .catch((err) => {
                this.setState({
                    [loadingStateVariableName]: false,
                    [errorStateVariableName]: err
                })
            });
    }

    isValidLocation() {
        return Object.keys(this.linkMap).indexOf(this.props.inventory) > -1 && this.props.facility_id !== "";
    }

    renderBanners(alerts) {
        if (this.state.bannerLoading) {
            return <Spinner />
        }

        if (!alerts || alerts.length === 0) {
            return <span>No results.</span>
        }

        return (
        <div>
            {alerts.map((alert, i) => (
            <div
                key={`banner-item_${i}`}
                className={alert.alert_level === 'WARNING' ? 'ia-block rec-notification-warning' : 'ia-block rec-notification-info'}>
                <div dangerouslySetInnerHTML={{ __html: alert.body}} />
                {alert.notes !== undefined && alert.notes.length > 0 &&
                <div><i>NOTE: {alert.notes}</i></div>
                }
            </div>
            ))}
        </div>
        );
    }

    renderList(notes, textKey, isLoading, setHTML) {
        if (isLoading) {
            return <Spinner />
        }

        if (!notes || notes.length === 0 || isEmpty(notes)) {
            return <span>No results.</span>
        }

        const elements = [];

        for(let i = 0; i < notes.length; i++) {
            let data = notes[i];

            if (textKey !== "") {
                data = get(notes[i], textKey, "");
            }

            if (setHTML) {
                data = <div dangerouslySetInnerHTML={{ __html: data }} />
            }
            elements.push(
                <li key={i}>
                    { data }
                </li>
            )
        }

        return (
            <ul>
                {elements}
            </ul>
        )
    }

    renderSeasonTable() {
        if(this.state.seasonsLoading) {
            return <Spinner />
        }

        if(!this.state.seasonsData) {
            return <span>No results.</span>
        }

        return (
        <table>
            <thead>
                <tr>
                <th>Season</th>
                <th>Dates</th>
                </tr>
            </thead>
            <tbody>
                {this.state.seasonsData.map((season, i) => (
                <tr key={`season-row_${i}`}>
                    <td>{season.season_type}</td>
                    <td>{`${moment(season.season_start).format('MMMM DD, YYYY')} - ${moment(season.season_end).format('MMMM DD, YYYY')}`}</td>
                </tr>
                ))}
            </tbody>
        </table>
        )
    }

    render() {
        if (!this.state.isExternalCSRLoggedIn) {
            return null;
        }

        if (!this.isValidLocation()) {
            return null;
        }

        // Seasons only apply for campgrounds
        let seasons = null;
        if (this.props.inventory === this.CAMPGROUND) {
            seasons = (
            <div>
                <Heading
                    appearance="h6"
                    headingLevel={3}>
                    Facility Seasons
                </Heading>

                {(this.state.seasonsError) ? <span>Error retrieving season information.</span> : this.renderSeasonTable()}
            </div>)
        }


        // Additional info is grabbed differently depending on inventory type
        let additionalInfo = null;
        if (this.props.inventory === this.CAMPGROUND) {
            additionalInfo = this.renderList(this.state.additionalData, "notice_text", this.state.additionalLoading, true)
        }
        else if (this.props.inventory === this.TICKET_FACILITY) {
            additionalInfo = this.renderList(this.state.additionalData, "description", this.state.additionalLoading, true)
        }
        else {
            additionalInfo = this.renderList(this.state.additionalData, "", this.state.additionalLoading)
        }

        const callCenterNotes = get(this.state.locationData, "location_notes", []);

        const optionalNotes = callCenterNotes.filter((note) => !note.mandatory);
        const mandatoryNotes = callCenterNotes.filter((note) => note.mandatory);

        return (
        <section className={cx('rec-order-detail-need-to-know', 'rec-order-detail-section')}>
            <Heading
                className="rec-section-header"
                appearance="h4"
                headingLevel={2}>
                CSR Facility Information
            </Heading>

            <div className="rec-order-detail-section-body">
                <Heading
                    appearance="h6"
                    headingLevel={3}>
                    Mandatory Call Center Read
                </Heading>
                {(this.state.locationError) ? <span>Error retrieving call center notes.</span> : this.renderList(mandatoryNotes, "note", this.state.locationLoading)}
                <Heading
                    appearance="h6"
                    headingLevel={3}>
                    External Banners
                </Heading>
                {(this.state.bannerError) ? <span>Error retrieving external banners.</span> : this.renderBanners(this.state.bannerData)}
                <Heading
                    appearance="h6"
                    headingLevel={3}>
                    Call Center Notes
                </Heading>
                {(this.state.locationError) ? <span>Error retrieving call center notes.</span> : this.renderList(optionalNotes, "note", this.state.locationLoading)}
                <Heading
                    appearance="h6"
                    headingLevel={3}>
                    Additional Information
                </Heading>
                {(this.state.additionalError) ? <span>Error retrieving additional information</span> : additionalInfo}
                {seasons}
            </div>
        </section>)
    }
}
